import { useCallback, useState } from 'react';
import type { CellComment, ReaderCellComment, SessionComment } from '@/api/client';
import { EditIcon, DeleteIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import '@/components/Messenger/shared/messages/ReplyQuote.css';
import { CellCommentContextMenu } from './CellCommentContextMenu';
import { linkifyPlainText } from '@/utils/linkifyPlainText';

export interface CellCommentsThreadItemProps {
  comment: CellComment | ReaderCellComment | SessionComment;
  authorName: string;
  timeLabel: string;
  /** Ожидание ответа сервера (оптимистичный комментарий) — спиннер вместо времени */
  deliveryPending?: boolean;
  edited: boolean;
  showEdit: boolean;
  showDelete: boolean;
  deletePending: boolean;
  onEdit: (c: CellComment | ReaderCellComment | SessionComment) => void;
  onDelete: (id: string) => void;
  onReply: (c: CellComment | ReaderCellComment | SessionComment) => void;
  onCommentContextMenuOpen: () => void;
  canReply?: boolean;
}

export function CellCommentsThreadItem({
  comment,
  authorName,
  timeLabel,
  deliveryPending = false,
  edited,
  showEdit,
  showDelete,
  deletePending,
  onEdit,
  onDelete,
  onReply,
  onCommentContextMenuOpen,
  canReply = true,
}: CellCommentsThreadItemProps) {
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);

  const rt = comment.reply_to;
  const parentAuthor = (rt?.author_name ?? '').trim() || 'Участник';
  const previewLine = rt ? (rt.body?.trim() ?? '').replace(/\r?\n/g, ' ') : '';

  const scrollToParent = useCallback(() => {
    if (!rt?.id) return;
    const el = document.querySelector(`[data-cell-comment-id="${rt.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('cell-comments-thread__item--highlighted');
    window.setTimeout(() => el.classList.remove('cell-comments-thread__item--highlighted'), 1500);
  }, [rt?.id]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onCommentContextMenuOpen();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
    },
    [onCommentContextMenuOpen]
  );

  const closeContextMenu = useCallback(() => setContextMenuPos(null), []);

  const hasActionButtons = showEdit || showDelete;

  return (
    <li
      className="cell-comments-thread__item"
      data-cell-comment-id={comment.id}
      onContextMenu={handleContextMenu}
    >
      <div className="cell-comments-thread__item-main">
        {rt ? (
          <button
            type="button"
            className="replyQuote replyQuote_peer cell-comments-thread__reply-quote"
            onClick={scrollToParent}
            aria-label={`Перейти к комментарию от ${parentAuthor}`}
          >
            <div className="replyQuote__bar" aria-hidden />
            <div className="replyQuote__content">
              <span className="replyQuote__sender">{parentAuthor}</span>
              <div className="replyQuote__preview">
                <span className="replyQuote__previewText">{previewLine || '…'}</span>
              </div>
            </div>
          </button>
        ) : null}
        <div className="cell-comments-thread__item-meta-row">
          <span className="cell-comments-thread__item-author">{authorName}</span>
          <span className="cell-comments-thread__item-sep" aria-hidden="true" />
          {deliveryPending ? (
            <span
              className="cell-comments-thread__item-send-spinner"
              role="status"
              aria-live="polite"
              aria-label="Отправка комментария"
              title="Отправка…"
            />
          ) : (
            <span className="cell-comments-thread__item-time">{timeLabel}</span>
          )}
          {edited ? (
            <>
              <span className="cell-comments-thread__item-sep" aria-hidden="true" />
              <span className="cell-comments-thread__item-edited" title="Изменено">
                ред.
              </span>
            </>
          ) : null}
        </div>
        <div className="cell-comments-thread__item-body">{linkifyPlainText(comment.body)}</div>
      </div>
      {hasActionButtons ? (
        <div className="cell-comments-thread__item-actions" role="group" aria-label="Действия с комментарием">
          {showEdit ? (
            <IconButton aria-label="Редактировать" size="s" variant="default" onClick={() => onEdit(comment)}>
              <EditIcon />
            </IconButton>
          ) : null}
          {showDelete ? (
            <IconButton
              aria-label="Удалить"
              size="s"
              variant="danger"
              disabled={deletePending}
              onClick={() => onDelete(comment.id)}
            >
              <DeleteIcon />
            </IconButton>
          ) : null}
        </div>
      ) : null}
      <CellCommentContextMenu
        comment={comment}
        anchorPosition={contextMenuPos}
        onClose={closeContextMenu}
        onReply={onReply}
        canReply={canReply}
      />
    </li>
  );
}
